import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Competition } from '../../types/interfaces';
import {
  CompetitionPermissions, RubricConfig, errorMessage, fetchRubricConfig, isHostAdminOrAbove,
} from '../../services/manageApi';
import ManageCompetitionVotesModal from '../ManageCompetitionVotesModal';
import ManageCompetitionScoringResultsModal from '../ManageCompetitionScoringResultsModal';
import ManageCompetitionEvaluationModal from '../ManageCompetitionEvaluationModal';
import ActionButton from './ActionButton';
import EvaluationSettingsForm from './EvaluationSettingsForm';
import RubricEditor from './RubricEditor';
import JudgesAllowanceSection from './JudgesAllowanceSection';
import DangerZone from './DangerZone';

interface Props {
  competition: Competition;
  permissions: CompetitionPermissions;
  canEdit: (field: string) => boolean;
  onCompetitionUpdated: (competition: Competition) => void;
  onDirtyChange: (section: string, dirty: boolean) => void;
  /** Returns true when leaving the page is fine (nothing unsaved, or the user confirmed). */
  confirmLeave: () => boolean;
}

const EvaluationTab: React.FC<Props> = ({
  competition, permissions, canEdit, onCompetitionUpdated, onDirtyChange, confirmLeave,
}) => {
  const navigate = useNavigate();
  const type = (competition.type || '').toLowerCase();
  const isMulti = type === 'multi';
  const supportsAutoEvaluation = type === 'code' || type === 'csv';
  const canManageHosts = isHostAdminOrAbove(permissions.role);
  const [rubricConfig, setRubricConfig] = useState<RubricConfig | null>(null);
  const [rubricError, setRubricError] = useState('');
  const [open, setOpen] = useState<null | 'results' | 'auto'>(null);
  const [judgingVersion, setJudgingVersion] = useState(0);
  const can = (action: string) => permissions.actions.includes(action);
  // After a reset or a deleted submission: reload judges, submissions and the rubric (which may unlock).
  const handleJudgingChanged = useCallback(() => setJudgingVersion((v) => v + 1), []);

  useEffect(() => {
    if (!isMulti) {
      setRubricConfig(null);
      return undefined;
    }
    let cancelled = false;
    setRubricError('');
    fetchRubricConfig(competition.id)
      .then((result) => { if (!cancelled) setRubricConfig(result); })
      .catch((err) => { if (!cancelled) setRubricError(errorMessage(err, 'Could not load the scoring configuration.')); });
    return () => {
      cancelled = true;
    };
  }, [competition.id, isMulti, judgingVersion]);

  const evaluationMode: 'rubric' | 'single_score' | 'pairwise' = isMulti
    ? (rubricConfig?.evaluation_mode ?? 'single_score')
    : 'pairwise';

  return (
    <>
      <EvaluationSettingsForm
        competition={competition}
        permissions={permissions}
        canEdit={canEdit}
        onSaved={onCompetitionUpdated}
        onDirtyChange={onDirtyChange}
      />
      {isMulti && rubricConfig && (
        <RubricEditor
          competitionId={competition.id}
          config={rubricConfig}
          canEdit={canEdit('rubric')}
          requiredRole={permissions.field_min_role.rubric}
          onSaved={setRubricConfig}
          onDirtyChange={onDirtyChange}
        />
      )}
      <JudgesAllowanceSection
        competitionId={competition.id}
        canManage={can('manage_judges')}
        refreshKey={judgingVersion}
        onDirtyChange={onDirtyChange}
      />

      <section className="manage-section" data-tour="manage-judging-actions">
        <h3>Judging &amp; results</h3>
        <div className="manage-competition-actions__stack">
          <ActionButton
            label="Go to judging page"
            onClick={() => { if (confirmLeave()) navigate(`/evaluations/${competition.id}`); }}
          />
          <ActionButton label="See evaluations" disabled={!canManageHosts || (isMulti && !rubricConfig)} onClick={() => setOpen('results')} />
          {supportsAutoEvaluation && (
            <ActionButton label="Auto evaluate / sync" disabled={!canManageHosts} onClick={() => setOpen('auto')} />
          )}
        </div>
        {!canManageHosts && (
          <p className="manage-competition-actions__hint">
            “See evaluations” and “Auto evaluate / sync” require platform admin or host admin privilege.
          </p>
        )}
        {rubricError && <p className="manage-error" role="alert">{rubricError}</p>}
      </section>
      {(can('reset_judging') || can('delete_submission')) && (
        <DangerZone
          competition={competition}
          canReset={can('reset_judging')}
          canDelete={can('delete_submission')}
          refreshKey={judgingVersion}
          onJudgingChanged={handleJudgingChanged}
        />
      )}

      {evaluationMode === 'pairwise' ? (
        <ManageCompetitionVotesModal isOpen={open === 'results'} onClose={() => setOpen(null)} competitionId={Number(competition.id)} />
      ) : (
        <ManageCompetitionScoringResultsModal
          isOpen={open === 'results'}
          onClose={() => setOpen(null)}
          competitionId={competition.id}
          mode={evaluationMode}
        />
      )}
      <ManageCompetitionEvaluationModal
        isOpen={open === 'auto'}
        onClose={() => setOpen(null)}
        competitionId={Number(competition.id)}
        competitionMode={(competition.competition_mode || 'individual') as 'individual' | 'team'}
      />
    </>
  );
};

export default EvaluationTab;
